/**
 * AnalysisTracker.js - HOC pour tracker automatiquement les analyses
 */

import React, { useEffect, useRef } from 'react';
import { useAnalysisTracking } from '../../hooks/useAnalytics';

/**
 * HOC pour wrapper les composants d'analyse
 */
export const withAnalysisTracking = (WrappedComponent) => {
  return (props) => {
    const {
      trackAnalysis,
      saveAnalysis,
      markAsConsidered,
      startAnalysis,
      completeAnalysis,
      resetAnalysis,
      currentAnalysisId,
      loading: trackingLoading
    } = useAnalysisTracking();

    const componentStartTime = useRef(null);

    // Démarrer le timer au montage du composant
    useEffect(() => {
      componentStartTime.current = startAnalysis();
      return () => {
        if (currentAnalysisId && componentStartTime.current) {
          const timeSpent = (Date.now() - componentStartTime.current) / 1000;
          if (timeSpent > 1) {
            markAsConsidered(currentAnalysisId, { timeSpentSeconds: timeSpent });
          }
        }
      };
    }, []);

    /**
     * Tracker une analyse effectuée
     */
    const onAnalysisComplete = async (analysisData) => {
      const trackingData = {
        analysisType: analysisData.type || analysisData.analysisType,
        medications: analysisData.medications || [],
        riskLevel: analysisData.riskLevel,
        findingsCount: analysisData.findingsCount || analysisData.findings?.length || 0,
        findings: analysisData.findings || [],
        patientContext: analysisData.patientContext || {},
        responseTime: analysisData.responseTime || 0,
        cacheHit: analysisData.cacheHit || false
      };

      const analysisId = await trackAnalysis(trackingData);

      return {
        analysisId,
        trackingComplete: !!analysisId
      };
    };

    /**
     * Tracker la sauvegarde
     */
    const onSaveAnalysis = async (analysisId, saveData) => {
      const id = analysisId || currentAnalysisId;
      if (!id) return null;

      return await saveAnalysis(id, {
        name: saveData?.name || 'Sauvegarde',
        notes: saveData?.notes || '',
        location: saveData?.location
      });
    };

    /**
     * Tracker la consultation
     */
    const onViewAnalysis = async (analysisId, viewData) => {
      const id = analysisId || currentAnalysisId;
      if (!id) return null;

      return await markAsConsidered(id, {
        timeSpentSeconds: viewData?.timeSpentSeconds || 0,
        userAction: viewData?.action || 'VIEW'
      });
    };

    /**
     * Terminer et tracker l'analyse
     */
    const onAnalysisFinish = async (analysisId) => {
      return await completeAnalysis(analysisId);
    };

    return (
        <WrappedComponent
            {...props}
            onAnalysisComplete={onAnalysisComplete}
            onSaveAnalysis={onSaveAnalysis}
            onViewAnalysis={onViewAnalysis}
            onAnalysisFinish={onAnalysisFinish}
            trackingLoading={trackingLoading}
            currentAnalysisId={currentAnalysisId}
            sessionId={useAnalysisTracking().sessionId?.current}
        />
    );
  };
};

export default withAnalysisTracking;